import { Helmet } from 'react-helmet-async';

const LAST_UPDATED = 'August 24, 2026';

function PrivacyPolicyPage() {
  return (
    <div className="how-to-use-page">
      <Helmet>
        <title>Privacy Policy - Zlux</title>
        <meta name="description" content="How Zlux collects, uses, and protects your data." />
        <link rel="canonical" href="https://zlux.asia/privacy" />
      </Helmet>
      <header className="how-to-hero">
        <p className="eyebrow">Legal</p>
        <h1 className="how-to-title">Privacy Policy</h1>
        <p className="how-to-hero-body">Last updated: {LAST_UPDATED}</p>
      </header>

      <div className="how-to-sections">
        <section className="card how-to-section">
          <div className="how-to-section-header"><h2>Overview</h2></div>
          <p>
            Zlux ("we", "our") is a personal finance and productivity app (web and mobile).
            This policy explains what information Zlux collects, why, and how you can
            control or remove it. Zlux is operated by an individual developer, not a
            registered company.
          </p>
        </section>

        <section className="card how-to-section">
          <div className="how-to-section-header"><h2>Information we collect</h2></div>
          <ul>
            <li><strong>Account information:</strong> username, email address, and password (stored as a one-way hash, never in plain text). If you sign in with Google, we receive your name, email, and profile photo from Google.</li>
            <li><strong>Financial data you enter:</strong> expenses, income, budgets, wallets, savings/activity goals, and checklists you create. This data is stored so the app can show it back to you; we do not sell or share it.</li>
            <li><strong>AI assistant conversations:</strong> messages you send to the in-app AI assistant are forwarded to Google's Gemini API to generate a response, and may result in the assistant creating budgets/goals/checklists on your behalf when you confirm an action.</li>
            <li><strong>Push notification token:</strong> if you enable notifications on the mobile app, we store a device token (via Expo's push service) to deliver reminders (e.g. unfinished checklists, subscription renewal).</li>
            <li><strong>Subscription/payment status:</strong> if you upgrade to Pro, we record the plan, price, and status of your subscription. Payments are processed by SePay (bank transfer) or Apple (In-App Purchase) - Zlux never sees or stores your card or bank login details directly.</li>
            <li><strong>Basic technical data:</strong> last login time, and standard web server logs (IP address, request timestamps) for security and troubleshooting.</li>
          </ul>
        </section>

        <section className="card how-to-section">
          <div className="how-to-section-header"><h2>How we use your information</h2></div>
          <p>Solely to operate the app: authenticate you, display your own data back to you, process your subscription, send the notifications you opted into, and respond to support requests. We do not sell your data or use it for third-party advertising.</p>
        </section>

        <section className="card how-to-section">
          <div className="how-to-section-header"><h2>Third parties we use</h2></div>
          <ul>
            <li><strong>Google</strong> - Sign-In authentication, and the Gemini API for the AI assistant.</li>
            <li><strong>SePay</strong> - matches bank transfers to activate a Pro subscription (web/Android checkout).</li>
            <li><strong>Apple</strong> - processes In-App Purchases on iOS; Zlux never receives your Apple ID payment details.</li>
            <li><strong>Expo</strong> - delivers push notifications to the mobile app.</li>
          </ul>
          <p>Each of these providers has its own privacy policy governing the data it processes on their systems.</p>
        </section>

        <section className="card how-to-section">
          <div className="how-to-section-header"><h2>Data retention and deletion</h2></div>
          <p>
            Your data is kept for as long as your account exists. You can request full
            account deletion (which permanently removes your profile and all associated
            expenses, budgets, wallets, goals, and checklists) at any time by emailing{' '}
            <a href="mailto:privacy@zlux.app">privacy@zlux.app</a> from the email address
            registered on your account. We will process deletion requests within 30 days.
          </p>
        </section>

        <section className="card how-to-section">
          <div className="how-to-section-header"><h2>Children's privacy</h2></div>
          <p>Zlux is not directed at children under 13, and we do not knowingly collect data from them.</p>
        </section>

        <section className="card how-to-section">
          <div className="how-to-section-header"><h2>Changes to this policy</h2></div>
          <p>If this policy changes materially, the "Last updated" date above will change and, where appropriate, we will notify you in-app.</p>
        </section>

        <section className="card how-to-section">
          <div className="how-to-section-header"><h2>Contact</h2></div>
          <p>Questions about this policy or your data: <a href="mailto:privacy@zlux.app">privacy@zlux.app</a>.</p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;
